import React from "react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, LineChart, Line,
} from "recharts";
import { TrendingUp, Sparkles, ShoppingBag, ArrowUpRight, ArrowDownRight } from "lucide-react";

/* ─── Mock data — small, deterministic ─── */
const REVENUE = [
  { d: "Mon", v: 24, t: 18 },
  { d: "Tue", v: 31, t: 22 },
  { d: "Wed", v: 28, t: 24 },
  { d: "Thu", v: 42, t: 30 },
  { d: "Fri", v: 51, t: 36 },
  { d: "Sat", v: 64, t: 48 },
  { d: "Sun", v: 72, t: 55 },
];

const CHANNELS = [
  { name: "Myntra",   v: 42 },
  { name: "Nykaa",    v: 31 },
  { name: "Ajio",     v: 24 },
  { name: "Amazon",   v: 28 },
  { name: "Flipkart", v: 19 },
  { name: "Blinkit",  v: 12 },
];

const SPARK = [
  { x: 0, y: 24 }, { x: 1, y: 31 }, { x: 2, y: 28 }, { x: 3, y: 42 },
  { x: 4, y: 51 }, { x: 5, y: 64 }, { x: 6, y: 72 }, { x: 7, y: 88 },
];

/** Compact KPI tile */
function Kpi({ label, value, delta, up = true, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
          {label}
        </div>
        <Icon size={14} className="text-indigo-400" />
      </div>
      <div className="font-mkt-display text-2xl font-semibold tracking-tight">{value}</div>
      <div className={`mt-1 inline-flex items-center gap-1 text-[11px] font-medium ${up ? "text-emerald-400" : "text-rose-400"}`}>
        {up ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
        {delta}
      </div>
    </div>
  );
}

/**
 * Full glassy dashboard preview — used in the Hero of the landing page
 * + reused on the Features page. No external image — pure SVG/Recharts.
 */
export default function DashboardPreview({ compact = false }) {
  return (
    <div className={`mkt-glass-strong p-5 sm:p-6 ${compact ? "" : "shadow-[0_30px_80px_-20px_rgba(79,70,229,0.45)]"}`}>
      {/* Mock browser chrome */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
        </div>
        <div className="px-3 py-1 rounded-full bg-white/[0.04] border border-white/[0.06] text-[10px] text-slate-400 font-mono">
          ecomcatalog.ai/seller
        </div>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-500/15 border border-indigo-500/30 text-[10px] text-indigo-300 font-semibold">
          <Sparkles size={10} /> AI ACTIVE
        </div>
      </div>

      {/* Top KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi label="Revenue" value="₹18.4L" delta="+24% WoW" up icon={TrendingUp} />
        <Kpi label="Live SKUs" value="1,284" delta="+128 this wk" up icon={ShoppingBag} />
        <Kpi label="Avg ROAS" value="4.6x" delta="+0.8x" up icon={ArrowUpRight} />
        <Kpi label="RTO %" value="11.2" delta="−2.4%" up icon={ArrowDownRight} />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 mt-3">
        {/* Revenue area chart */}
        <div className="lg:col-span-3 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                Revenue vs Target
              </div>
              <div className="font-mkt-display text-base font-semibold">
                Last 7 days · all marketplaces
              </div>
            </div>
            <span className="text-[10px] text-emerald-400 font-mono">▲ on track</span>
          </div>
          <div className="h-[170px]">
            <ResponsiveContainer>
              <AreaChart data={REVENUE} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#4F46E5" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="tgt" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#06B6D4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis dataKey="d" stroke="#64748B" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748B" fontSize={10} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{
                    background: "#0F172A",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 12, fontSize: 11, color: "#F8FAFC",
                  }}
                  cursor={{ stroke: "rgba(255,255,255,0.1)" }}
                />
                <Area dataKey="t" stroke="#06B6D4" strokeWidth={1.5} fill="url(#tgt)" strokeDasharray="4 4" />
                <Area dataKey="v" stroke="#A78BFA" strokeWidth={2.2} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Channels bar chart */}
        <div className="lg:col-span-2 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4">
          <div className="mb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
              GMV by channel
            </div>
            <div className="font-mkt-display text-base font-semibold">Top 6 marketplaces</div>
          </div>
          <div className="h-[170px]">
            <ResponsiveContainer>
              <BarChart data={CHANNELS} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                <defs>
                  <linearGradient id="bar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#818CF8" />
                    <stop offset="100%" stopColor="#4F46E5" />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis dataKey="name" stroke="#64748B" fontSize={9} tickLine={false} axisLine={false} interval={0} />
                <YAxis stroke="#64748B" fontSize={10} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{
                    background: "#0F172A",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 12, fontSize: 11, color: "#F8FAFC",
                  }}
                  cursor={{ fill: "rgba(255,255,255,0.04)" }}
                />
                <Bar dataKey="v" fill="url(#bar)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* AI suggestion strip */}
      <div className="mt-3 rounded-2xl border border-indigo-500/20 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-cyan-500/5 p-4 flex items-start gap-3">
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 grid place-items-center shrink-0">
          <Sparkles size={14} className="text-white" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-300 font-bold mb-1">
            AI Suggestion
          </div>
          <div className="text-sm text-slate-200 font-medium leading-snug">
            "Anouk Pink Kurti" is converting 2.4× the catalog avg —
            <span className="text-indigo-200"> scale ad budget by ₹3,500/day for next 7 days.</span>
          </div>
        </div>
        <div className="hidden sm:block h-[36px] w-[80px] shrink-0">
          <ResponsiveContainer>
            <LineChart data={SPARK}>
              <Line dataKey="y" stroke="#C4B5FD" strokeWidth={2} dot={false} type="monotone" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
